import {
  Box,
  Button,
  FormHelperText,
  Grid,
  MenuItem,
  Typography,
  TextField,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { attemptAsync } from 'es-toolkit/util';
import { enqueueSnackbar } from 'notistack';
import * as Yup from 'yup';
import { useConfirm } from 'components/ConfirmDialog';
import { LatLng, MapLocation } from 'shared/types';
import { PlaceAddress, PlaceIcon, Place } from 'features/places/places.types';
import { placeTypeIcons } from 'features/mapping/icons';
import { SearchBar } from 'features/mapping/components/SearchBar';
import { Map } from 'features/mapping/components/Map';
import { LOCATION_ZOOM_LEVEL } from 'features/mapping/mapping.constants';
import { MapType } from 'features/mapping/mapTypePolicy';
import { useMapState } from 'state/useMapState';
import { IncidentMapStateUpdate } from 'features/mapping/mapping.types';
import {
  getLatLngLocationName,
  getRandomId,
  hasValidCoordinates,
} from 'shared/utils';
import { useColorScheme } from 'hooks/useColorScheme';
import { usePlaces } from 'features/places/hooks/usePlaces';
import { useMapInstance } from 'shared/map-exports';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { LocationMarker } from 'features/mapping/components/LocationMarker';
import './PlaceForm.css';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useLocationState } from 'hooks/useLocationState';

type PlaceFormProps = {
  initialPlace?: Place;
  initialAddress?: PlaceAddress;
};

type FormValues = Omit<Place, 'address' | 'id'> & {
  address: PlaceAddress | null;
};

const getDefaultMapCenter = (
  incidentMap: { center: { lat: number; lng: number } },
  placeAddress: PlaceAddress | null,
): LatLng => {
  const lat = placeAddress?.coordinates.latitude ?? incidentMap.center.lat;
  const lng = placeAddress?.coordinates.longitude ?? incidentMap.center.lng;

  if (hasValidCoordinates({ lat, lng })) {
    return { lat, lng };
  }

  return Map.DefaultConfig.center;
};

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(1),
  },
  mapContainer: {
    width: '100%',
    height: '31vh',
    position: 'relative',
  },
  noTouchEvents: {
    pointerEvents: 'none',
  },
  mapHelperText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  helperText: {
    margin: '3px 14px 0px',
  },
}));

const validationSchema = Yup.object({
  name: Yup.string().required('Required'),
  icon: Yup.string().required('Required'),
  address: Yup.object({
    name: Yup.string(),
    coordinates: Yup.object({
      latitude: Yup.number(),
      longitude: Yup.number(),
    }),
    bbox: Yup.array(Yup.number()),
  })
    .required('Required')
    .nullable(),
});

type MapCenterTarget = {
  coords: LatLng;
  animate: boolean;
};

type MapCenterTrackerProps = {
  centerTarget: MapCenterTarget | null;
  zoom: number;
  onChange: (lat: number, lng: number) => void;
};

/**
 * Drives the form map's view and reports the coordinate to save via `onChange`.
 *
 * The map reuses the app's shared GL instance (`reuseMaps`), so `moveend` also
 * fires for programmatic re-centers and lagging events from the home map.
 * Selections report the selected coordinate directly; only genuine user
 * gestures (which carry an `originalEvent`) report the live center.
 */
const MapCenterTracker = (props: MapCenterTrackerProps): null => {
  const { centerTarget, zoom, onChange } = props;
  const map = useMapInstance();
  const hasUserMovedRef = useRef(false);

  // Re-center and anchor on the selected/initial coordinate. Done here because
  // the shared GL instance ignores the initial `center` prop on reuse. Camera
  // moves don't need tiles, so this runs before the map finishes loading.
  useEffect(() => {
    if (!map || !centerTarget || !hasValidCoordinates(centerTarget.coords)) {
      return undefined;
    }

    const { coords, animate } = centerTarget;
    onChange(coords.lat, coords.lng);
    hasUserMovedRef.current = false;

    const nextView = {
      center: [coords.lng, coords.lat] as [number, number],
      zoom,
    };
    if (animate) {
      map.easeTo(nextView);
    } else {
      map.jumpTo(nextView);
    }

    // On a cold start the map's own initial view can override an early move,
    // so re-apply once it loads unless the user has moved the map since.
    const reapplyOnLoad = (): void => {
      if (!hasUserMovedRef.current) map.jumpTo(nextView);
    };
    map.once('load', reapplyOnLoad);

    // The GL instance outlives this form; don't move the home map after leaving.
    return () => {
      map.off('load', reapplyOnLoad);
    };
  }, [centerTarget, zoom, map, onChange]);

  // Adopt the live center only on genuine user gestures; ignore programmatic /
  // lagging events (no `originalEvent`).
  useMapEvent(
    'moveend',
    (event) => {
      if (event.originalEvent) {
        hasUserMovedRef.current = true;
        const { lat, lng } = event.target.getCenter();
        onChange(lat, lng);
      }
    },
    map,
    true,
  );

  return null;
};

export const PlaceForm = (props: PlaceFormProps): JSX.Element => {
  const { initialPlace, initialAddress } = props;
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { incidentMapState, updateIncidentMapState } = useMapState();
  const { addMapPlace, updateMapPlace, deleteMapPlace } = usePlaces();
  const history = useHistory();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  const initialAddressValue = initialPlace?.address ?? initialAddress ?? null;

  // The coordinate to save: the form map's center (pin), driven by
  // MapCenterTracker.
  const mapCenterRef = useRef<LatLng>(
    getDefaultMapCenter(incidentMapState, initialAddressValue),
  );

  const handleMapCenterChange = useCallback((lat: number, lng: number) => {
    mapCenterRef.current = { lat, lng };
  }, []);

  // New object per selection (and for the initial position) so the map
  // re-centers even when the same place is selected again.
  const [centerTarget, setCenterTarget] = useState<MapCenterTarget | null>(
    initialAddressValue
      ? {
          coords: {
            lat: initialAddressValue.coordinates.latitude,
            lng: initialAddressValue.coordinates.longitude,
          },
          animate: false,
        }
      : null,
  );

  const { isDarkMode } = useColorScheme();
  const confirm = useConfirm();

  const initialValues: FormValues = {
    name: initialPlace?.name || '',
    icon: initialPlace?.icon || 'home',
    address: initialAddressValue,
  };

  const [isDeleting, setIsDeleting] = useState(false);

  const {
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const name = watch('name');
  const icon = watch('icon');
  const address = watch('address');

  const locationType = address?.type || initialAddressValue?.type;

  const { fromRoute } = useLocationState();

  const showSaveError = (): void => {
    enqueueSnackbar(t('common.unknownErrorTryAgain'), { variant: 'error' });
  };

  // Waits for the save so the form, and what was entered, stays open if it fails
  const handleAddPlace: SubmitHandler<FormValues> = async (values) => {
    const addressLocation = {
      ...values.address,
      coordinates: {
        latitude: mapCenterRef.current.lat,
        longitude: mapCenterRef.current.lng,
      },
    } as PlaceAddress;

    if (locationType === 'LatLngLocation') {
      addressLocation.name = getLatLngLocationName(
        mapCenterRef.current.lat,
        mapCenterRef.current.lng,
      );
    }

    const placeLocation = {
      ...values,
      id: initialPlace?.id || getRandomId(),
      address: addressLocation,
    } as Place;

    if (initialPlace) {
      const [updateError] = await attemptAsync(() =>
        updateMapPlace(placeLocation),
      );
      if (updateError) {
        showSaveError();
        return;
      }

      if (pinImprovements) {
        history.push(`/place/${placeLocation.id}`);
      } else {
        history.goBack();
      }

      return;
    }

    const [addError] = await attemptAsync(() => addMapPlace(placeLocation));
    if (addError) {
      showSaveError();
      return;
    }

    if (pinImprovements) {
      history.push(`/place/${placeLocation.id}`);
    } else {
      const mapLocation: MapLocation = {
        name: placeLocation.name,
        lat: placeLocation.address.coordinates.latitude,
        lng: placeLocation.address.coordinates.longitude,
        bbox: placeLocation.address.bbox,
        place: true,
      };

      history.replace('/', mapLocation);
    }
  };

  const handleDeleteMapPlace = async (placeId: number): Promise<void> => {
    const deleteConfirmed = await confirm(t('placeForm.deletePrompt'));

    if (!deleteConfirmed) return;

    setIsDeleting(true);
    const [deleteError] = await attemptAsync(() => deleteMapPlace(placeId));
    setIsDeleting(false);
    if (deleteError) {
      showSaveError();
      return;
    }

    if (pinImprovements) {
      // Don't return to `/place/<id>` if it no longer exists
      if (fromRoute && !fromRoute.includes(`/place/${placeId}`)) {
        history.push(fromRoute);
      } else {
        history.push('/');
      }
    } else {
      history.goBack();
    }
  };

  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate) => {
      updateIncidentMapState(viewport);
    },
    [updateIncidentMapState],
  );

  const getInitialSearchTerm = (): string | undefined => {
    if (locationType === 'LatLngLocation') {
      return getLatLngLocationName(
        mapCenterRef.current.lat,
        mapCenterRef.current.lng,
      );
    }

    return initialAddressValue?.name;
  };

  const initialName = getInitialSearchTerm();

  return (
    <Box className={classes.contentContainer}>
      <Box
        className={cx(classes.mapContainer, !address && classes.noTouchEvents)}
        data-testid="place-form-map"
      >
        <Map
          // `center` is consumed only as the map's initial view state, so
          // reading the ref's current value here is intentional.
          center={mapCenterRef.current}
          mapType={MapType.PlaceForm}
          cursor={address ? 'auto' : 'not-allowed'}
          interactive={Boolean(address)}
        >
          <MapCenterTracker
            centerTarget={centerTarget}
            zoom={LOCATION_ZOOM_LEVEL}
            onChange={handleMapCenterChange}
          />
          <Map.MapEvents onViewportChange={onViewportChange} />
        </Map>
        {address && (
          <LocationMarker
            name={name || initialName || address.name}
            placeIcon={icon}
          />
        )}
      </Box>

      <Typography
        variant="subtitle1"
        color="textSecondary"
        className={classes.mapHelperText}
      >
        {t('placeForm.mapHelperText')}
      </Typography>

      <FormWithoutEnter onSubmit={handleSubmit(handleAddPlace)} noValidate>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={12}>
            <Controller
              name="address"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const hasError = Boolean(fieldState.error);
                return (
                  <>
                    <SearchBar
                      formField
                      label={t('placeForm.inputs.address.label')}
                      name={field.name}
                      error={hasError}
                      onBlur={field.onBlur}
                      onLocationChange={(location): void | undefined => {
                        if (!location) {
                          field.onChange(null);
                          return;
                        }
                        field.onChange({
                          name: location.name,
                          formattedAddress: location.formattedAddress,
                          coordinates: {
                            latitude: location.lat,
                            longitude: location.lng,
                          },
                          bbox: location.bbox,
                          type: location.type,
                        } as PlaceAddress);
                        // Anchor here too, so saving never depends on the map.
                        handleMapCenterChange(location.lat, location.lng);
                        // Fresh object so re-selecting the same place still
                        // re-centers.
                        setCenterTarget({
                          coords: { lat: location.lat, lng: location.lng },
                          animate: true,
                        });
                      }}
                      initialSearchTerm={initialName}
                      ref={field.ref}
                    />
                    {hasError && (
                      <FormHelperText error className={classes.helperText}>
                        {fieldState.error?.message}
                      </FormHelperText>
                    )}
                  </>
                );
              }}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, ...muiFieldProps } = field;
                return (
                  <TextField
                    id="field-control-name"
                    label={t('placeForm.inputs.name.label')}
                    fullWidth
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      t('placeForm.inputs.name.helperText')
                    }
                    required
                  />
                );
              }}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              name="icon"
              control={control}
              render={({ field, fieldState }): JSX.Element => {
                const { ref, ...muiFieldProps } = field;
                return (
                  <TextField
                    select
                    id="field-control-icon"
                    label={t('placeForm.inputs.icon.label')}
                    fullWidth
                    {...muiFieldProps}
                    inputRef={ref}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    required
                    sx={{ '.MuiSelect-select': { display: 'flex' } }}
                  >
                    {(Object.keys(placeTypeIcons) as PlaceIcon[]).map(
                      (placeIcon: PlaceIcon) => {
                        const Icon = placeTypeIcons[placeIcon];
                        return (
                          <MenuItem key={placeIcon} value={placeIcon}>
                            <img
                              src={Icon.data}
                              alt={Icon.name}
                              data-testid={`${placeIcon}-icon`}
                              style={{
                                filter: isDarkMode ? undefined : 'invert(100%)',
                              }}
                            />
                          </MenuItem>
                        );
                      },
                    )}
                  </TextField>
                );
              }}
            />
          </Grid>

          <Grid size={12}>
            <Typography>
              <b>{t('addPlace.privatePlaces')}</b>
            </Typography>
          </Grid>

          <Grid size={12}>
            <Button
              fullWidth
              size="large"
              type="submit"
              loading={isSubmitting}
              disabled={isDeleting}
            >
              {t('common.save')}
            </Button>
          </Grid>

          {initialPlace && (
            <Grid size={12}>
              <Button
                fullWidth
                size="large"
                onClick={() => handleDeleteMapPlace(initialPlace.id)}
                sx={{ marginBottom: 2 }}
                color="error"
                loading={isDeleting}
                disabled={isSubmitting}
              >
                {t('common.delete')}
              </Button>
            </Grid>
          )}
        </Grid>
      </FormWithoutEnter>
    </Box>
  );
};
